import { schedulerApi } from "../services/api";
import { useNavigate } from "react-router-dom";
import { useEffect, useCallback, useRef, useMemo } from "react";
import { isElectron, getTimezoneOffset } from "../util/ElectronDetection";
import localNotificationService from "../services/LocalNotificationService";
import { useQueryClient } from "@tanstack/react-query";
import { getVideoByPreference } from "../util/videoHelpers";
import { useAuthStore } from "../stores/zustand";

export const useLocalNotifications = (options = {}) => {
  const {
    enabled = true,
    autoStartSync = true,
    wasAutoLogin = false,
    homeData = null,
  } = options;

  const queryClient = useQueryClient();
  const email = useAuthStore((state) => state.user?.email);
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);

  const scheduleDataRef = useRef(null);

  const hookId = useRef(`hook-${Math.random().toString(36).substr(2, 9)}`);

  const navigate = useNavigate();
  const syncStartedRef = useRef(false);
  const isInitializedRef = useRef(false);

  const fetchScheduleData = useCallback(async () => {
    try {
      const timezone = getTimezoneOffset();
      return await schedulerApi.getHomeData(timezone);
    } catch (error) {
      console.error(
        "[useLocalNotifications] Error fetching schedule data:",
        error,
      );
      throw error;
    }
  }, []);

  const enrichNotificationsWithVideoId = useCallback(
    (notifications) => {
      const cachedVideos = queryClient.getQueryData([
        "videos",
        email,
        preferredLanguage,
      ]);
      if (!cachedVideos) return notifications;

      const allVideos = [
        ...(cachedVideos.allVideos || []),
        ...(cachedVideos.physioVideos || []),
      ];
      if (allVideos.length === 0) return notifications;

      return notifications.map((notification) => ({
        ...notification,
        schedules: (notification.schedules || []).map((schedule) => {
          if (schedule.video?.id) return schedule; // already has a video

          const matchedVideo = getVideoByPreference(
            allVideos,
            notification.preferenceId,
            schedule.id,
          );

          return matchedVideo
            ? {
                ...schedule,
                video: {
                  id: matchedVideo.externalId,
                  title: matchedVideo.title,
                },
              }
            : schedule;
        }),
      }));
    },
    [queryClient, email, preferredLanguage],
  );

  const scheduleNotifications = useCallback(
    async (notifications) => {
      if (!isElectron() || !enabled) return;
      try {
        await localNotificationService.scheduleNotifications(notifications);
      } catch (error) {
        console.error(
          "[useLocalNotifications] Error scheduling notifications:",
          error,
        );
      }
    },
    [enabled],
  );

  const startSync = useCallback(() => {
    if (!isElectron() || !enabled || syncStartedRef.current) return;
    localNotificationService.startPeriodicSync(fetchScheduleData, wasAutoLogin);
    syncStartedRef.current = true;
  }, [enabled, fetchScheduleData, wasAutoLogin]);

  const syncNow = useCallback(async () => {
    if (!isElectron() || !enabled) return;
    try {
      await localNotificationService.syncWithServer(fetchScheduleData);
    } catch (error) {
      console.error("[useLocalNotifications] Manual sync failed:", error);
    }
  }, [enabled, fetchScheduleData]);

  const triggerImmediateSync = useCallback(async () => {
    if (!isElectron() || !enabled) return;
    try {
      await localNotificationService.triggerImmediateSync();
    } catch (error) {
      console.error("[useLocalNotifications] Immediate sync failed:", error);
    }
  }, [enabled]);

  const getStatus = useCallback(() => localNotificationService.getStatus(), []);

  const clearNotifications = useCallback(() => {
    if (!isElectron()) return;
    localNotificationService.clearAllNotifications();
  }, []);

  // Schedule notifications whenever homeData changes (create/edit/delete)
  useEffect(() => {
    if (!isElectron() || !enabled || !homeData?.notifications) return;

    scheduleDataRef.current = homeData;
    const enriched = enrichNotificationsWithVideoId(homeData.notifications);
    scheduleNotifications(enriched);

    // Start sync if not already started
    if (autoStartSync && !syncStartedRef.current) {
      startSync();
    }
  }, [homeData, enabled, scheduleNotifications, autoStartSync, startSync]);

  useEffect(() => {
    if (!isElectron() || !enabled) return;

    const handleNavigation = (event) => {
      const { url, videoId, preferenceName } = event.detail;
      if (event.detail._handled) {
        return;
      }
      event.detail._handled = true;

      if (url) {
        if (
          isElectron() &&
          (videoId === null || videoId === "null") &&
          preferenceName
        ) {
          const enhancedUrl = `${url}&preferenceName=${encodeURIComponent(
            preferenceName,
          )}`;
          navigate(enhancedUrl);
        } else {
          navigate(url);
        }
      } else {
        console.warn(
          `[useLocalNotifications] [${hookId.current}] No URL provided in navigation event`,
        );
      }
    };

    window.addEventListener("local-notification-navigate", handleNavigation);
    return () =>
      window.removeEventListener(
        "local-notification-navigate",
        handleNavigation,
      );
  }, [navigate, enabled]);

  useEffect(() => {
    if (isElectron() && enabled) {
      localNotificationService.registerHookInstance();
      return () => {
        localNotificationService.unregisterHookInstance();
        syncStartedRef.current = false;
        isInitializedRef.current = false;
      };
    }
  }, [enabled]);

  const returnValue = useMemo(
    () => ({
      scheduleNotifications,
      startSync,
      syncNow,
      triggerImmediateSync,
      clearNotifications,
      getStatus,
      isElectron: isElectron(),
      isEnabled: enabled && isElectron(),
      isInitialized: isInitializedRef.current,
      isSyncStarted: syncStartedRef.current,
    }),
    [
      scheduleNotifications,
      startSync,
      syncNow,
      triggerImmediateSync,
      clearNotifications,
      getStatus,
      enabled,
    ],
  );

  return returnValue;
};

export const useSimpleLocalNotifications = () => {
  return useLocalNotifications({ enabled: true, autoStartSync: true });
};

export default useLocalNotifications;
